import { useState, useCallback } from 'react';
import { GridRowModel } from '@mui/x-data-grid';

export interface EditGridPromiseInfo {
  resolve: (row: GridRowModel) => void;
  reject: (row: GridRowModel) => void;
  updatedRow: GridRowModel;
  originalRow: GridRowModel;
}

export interface RowMutations {
  updatedValue: string | number | number[] | { id: number; name: string } | null;
  editedKey: string;
  updatedLabel: string;
  originalLabel: string;
}

const initialRowMutationState: RowMutations = {
  updatedValue: '',
  editedKey: '',
  updatedLabel: '',
  originalLabel: ''
};

export const useRowEdit = (
  calculateRowEdit: (updatedRow: GridRowModel, originalRow: GridRowModel) => RowMutations | null
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editPromiseInfo, setEditPromiseInfo] = useState<EditGridPromiseInfo | null>(null);
  // these mutations are utilized for displaying what edits are made (and what the previous value was)
  const [rowMutations, setRowMutations] = useState<RowMutations>(initialRowMutationState);

  const resetRowMutations = useCallback(() => setRowMutations(initialRowMutationState), []);

  const processRowUpdate = useCallback(
    (updatedRow: GridRowModel, originalRow: GridRowModel) => {
      return new Promise<GridRowModel>((resolve, reject) => {
        const handleUpdate = async () => {
          try {
            setLoading(true);
            const rowEdit = calculateRowEdit(updatedRow, originalRow);

            if (!rowEdit) {
              resetRowMutations();
            }

            if (rowEdit) {
              setRowMutations(rowEdit);
              setEditPromiseInfo({ resolve, reject, updatedRow, originalRow });
            } else {
              resolve(originalRow);
            }
          } catch (error) {
            setError('Something went wrong: ' + error);
            reject(error);
          } finally {
            setLoading(false);
          }
        };
        handleUpdate();
      });
    },
    [calculateRowEdit, resetRowMutations]
  );

  const handleProcessRowUpdateError = useCallback(() => {
    setEditPromiseInfo(null);
    resetRowMutations();
    setError('Error processing grid data edit.');
  }, [resetRowMutations]);

  return {
    processRowUpdate,
    handleProcessRowUpdateError,
    editPromiseInfo,
    setEditPromiseInfo,
    resetRowMutations,
    rowMutations,
    loading,
    error
  };
};
